import { useQuery } from "@tanstack/react-query";
import { Navigate } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import { PARTNER_START } from "utils/referralRouting";
import { fetchPartnerMe } from "../api/endpoints/me";
import { partnerQueryKeys } from "../api/queryKeys";
import TermsGate from "../terms/TermsGate";

/**
 * Gate for every /partner route.
 *
 * The backend is the authority: it answers 401 when nobody is signed in and 403 when the caller has
 * no active partner profile. This component only translates those into screens, so a tampered
 * localStorage cannot open the portal.
 *
 * A 403 sends everyone to the start screen. The backend decides there whether this person can finish
 * their profile themselves (a colleague, or a partner whose referrals were migrated before the portal
 * existed) or must use their invitation link or the office, and the screen shows which.
 *
 * An active partner still passes through the terms gate before any page renders.
 */
const RequireReferralPartner = ({ children }) => {
  const { data, error, isLoading } = useQuery(partnerQueryKeys.me(), fetchPartnerMe, {
    retry: false,
    staleTime: 60_000,
  });

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <CircularProgress />
      </div>
    );
  }

  const status = error?.response?.status;
  if (status === 401) return <Navigate to="/login" replace />;
  if (status === 403) return <Navigate to={PARTNER_START} replace />;
  if (error) return <Navigate to="/login" replace />;
  if (!data) return <Navigate to="/login" replace />;

  return <TermsGate>{children}</TermsGate>;
};

export default RequireReferralPartner;
